import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { ScrollArea } from '../components/ui/scroll-area';
import { Textarea } from '../components/ui/textarea';
import { Separator } from '../components/ui/separator';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Users, Plus, Search, Upload, Download, Loader2, Phone, Mail, 
  Star, Edit, Trash2, FileSpreadsheet, Check, X, User, Megaphone, History,
  Receipt, Sparkles, Wallet, FileText,
} from 'lucide-react';
import { toast } from 'sonner';
import { formatDate, formatDateTime } from '../utils/dateFormat';

import { useStore } from '../context/StoreContext';
const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Customers = () => {
  const { storeName } = useStore();
  const { getAuthHeader } = useAuth();
  const [customers, setCustomers] = useState([]);
  const [stats, setStats] = useState({ total: 0, marketing_subscribers: 0, total_loyalty: 0, active_members: 0, owing_customers: 0 });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [importProgress, setImportProgress] = useState(null);
  const [isImportDialogOpen, setIsImportDialogOpen] = useState(false);
  const [isExportDialogOpen, setIsExportDialogOpen] = useState(false);
  const [formLoading, setFormLoading] = useState(false);
  const [importData, setImportData] = useState('');
  const [importPreview, setImportPreview] = useState([]);
  const [importStep, setImportStep] = useState(1);
  const [exporting, setExporting] = useState(false);
  
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    phone: '',
    email: '',
    loyalty_points: 0,
    marketing_opt_in: false, notes: ''
  });
  
  const [editingCustomer, setEditingCustomer] = useState(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);

  // Purchase history state
  const [historyCustomer, setHistoryCustomer] = useState(null);
  const [historySales, setHistorySales] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  // Store credit dialog state
  const [creditCustomer, setCreditCustomer] = useState(null);
  const [creditAmount, setCreditAmount] = useState('');
  const [creditReason, setCreditReason] = useState('');
  const [creditPin, setCreditPin] = useState('');
  const [creditHistory, setCreditHistory] = useState([]);
  const [creditHistoryLoading, setCreditHistoryLoading] = useState(false);
  const [creditSubmitting, setCreditSubmitting] = useState(false);
  const [creditMode, setCreditMode] = useState('add');

  // ── Add Past Purchase (retroactive loyalty attach) ──────────────────────
  // When customers forget to give their phone at the till, staff can later
  // attach the past sale to the customer's loyalty here.
  const [attachOpen, setAttachOpen] = useState(false);
  const [attachMode, setAttachMode] = useState('recent'); // 'receipt' | 'datetime' | 'recent'
  const [attachSearch, setAttachSearch] = useState({
    sale_id: '',
    start_date: '',
    end_date: '',
    min_amount: '',
    max_amount: '',
    store: '',
  });
  const [attachResults, setAttachResults] = useState([]);
  const [attachLoading, setAttachLoading] = useState(false);
  const [attaching, setAttaching] = useState(null); // sale_id currently attaching
  const [pinPromptSaleId, setPinPromptSaleId] = useState(null);
  const [pinValue, setPinValue] = useState('');

  // Points rule preview: $100 spent = 3 points (3% rounded down).
  const previewPoints = (amount) => Math.floor((amount || 0) * 0.03);

  const runAttachSearch = async (modeOverride) => {
    const mode = modeOverride || attachMode;
    setAttachLoading(true);
    try {
      const params = {};
      if (mode === 'receipt') {
        if (!attachSearch.sale_id.trim()) {
          toast.error('Enter a receipt / sale ID');
          setAttachLoading(false);
          return;
        }
        params.sale_id = attachSearch.sale_id.trim();
      } else if (mode === 'datetime') {
        if (attachSearch.start_date) params.start_date = attachSearch.start_date;
        if (attachSearch.end_date) params.end_date = attachSearch.end_date;
        if (attachSearch.min_amount) params.min_amount = parseFloat(attachSearch.min_amount);
        if (attachSearch.max_amount) params.max_amount = parseFloat(attachSearch.max_amount);
        if (attachSearch.store) params.store = attachSearch.store;
        params.limit = 100;
      } else {
        // recent: unattached only, last 30 default
        params.limit = 50;
      }
      const res = await axios.get(`${API_URL}/sales/unattached`, {
        headers: getAuthHeader(),
        params,
      });
      setAttachResults(res.data || []);
      if ((res.data || []).length === 0) {
        toast.info('No sales matched your search');
      }
    } catch (e) {
      toast.error('Search failed: ' + (e.response?.data?.detail || e.message));
    } finally {
      setAttachLoading(false);
    }
  };

  const attachSaleToCustomer = async (saleId, managerPin = null) => {
    if (!historyCustomer) return;
    setAttaching(saleId);
    try {
      const body = { customer_id: historyCustomer.id };
      if (managerPin) body.manager_pin = managerPin;
      const res = await axios.post(
        `${API_URL}/sales/${saleId}/attach-customer`,
        body,
        { headers: getAuthHeader() }
      );
      const d = res.data;
      if (d.already_attached) {
        toast.info('Already linked to this customer — no points added.');
      } else {
        toast.success(
          `Attached! +${d.points_credited} loyalty points added to ${d.customer_name}` +
          (d.re_attach ? ' (re-attached with manager PIN)' : '')
        );
      }
      // Refresh history + remove from results + refresh customer list points
      setAttachResults((prev) => prev.filter((s) => s.id !== saleId));
      setPinPromptSaleId(null);
      setPinValue('');
      // Reload history
      const hist = await axios.get(
        `${API_URL}/customers/${historyCustomer.id}/purchases`,
        { headers: getAuthHeader() }
      );
      setHistorySales(hist.data || []);
      fetchCustomers(); fetchStats();
    } catch (e) {
      const detail = e.response?.data?.detail || e.message;
      if (e.response?.status === 403 && detail.toLowerCase().includes('pin')) {
        setPinPromptSaleId(saleId);
      } else {
        toast.error('Attach failed: ' + detail);
      }
    } finally {
      setAttaching(null);
    }
  };

  const openHistoryDialog = async (customer) => {
    setHistoryCustomer(customer);
    setHistorySales([]);
    setHistoryLoading(true);
    // Reset retro-attach panel for each customer
    setAttachOpen(false);
    setAttachMode('recent');
    setAttachResults([]);
    setAttachSearch({ sale_id: '', start_date: '', end_date: '', min_amount: '', max_amount: '', store: '' });
    setPinPromptSaleId(null);
    setPinValue('');
    try {
      const res = await axios.get(`${API_URL}/customers/${customer.id}/purchases`, { headers: getAuthHeader() });
      setHistorySales(res.data || []);
    } catch (e) {
      toast.error('Failed to load purchase history');
    } finally {
      setHistoryLoading(false);
    }
  };

  const openStoreCreditDialog = async (customer) => {
    setCreditCustomer(customer);
    setCreditAmount('');
    setCreditReason('');
    setCreditPin('');
    setCreditMode('add');
    setCreditHistory([]);
    setCreditHistoryLoading(true);
    try {
      const res = await axios.get(`${API_URL}/customers/${customer.id}/store-credit/history`, { headers: getAuthHeader() });
      setCreditHistory(res.data || []);
    } catch (e) {
      toast.error('Failed to load store credit history');
    } finally {
      setCreditHistoryLoading(false);
    }
  };

  const handleAddStoreCredit = async (e) => {
    e.preventDefault();
    const amt = parseFloat(creditAmount);
    if (!amt || amt <= 0) {
      toast.error('Enter a valid amount');
      return;
    }
    if (!creditPin.trim()) {
      toast.error('Manager PIN is required');
      return;
    }
    setCreditSubmitting(true);
    try {
      const endpoint = creditMode === 'debt' ? 'debt' : 'add';
      const res = await axios.post(`${API_URL}/customers/${creditCustomer.id}/store-credit/${endpoint}`, {
        amount: amt,
        reason: creditReason,
        manager_pin: creditPin,
      }, { headers: getAuthHeader() });
      if (creditMode === 'debt') {
        toast.warning(`$${amt.toFixed(2)} debt recorded — ${creditCustomer.name} now owes, balance $${res.data.store_credit.toFixed(2)}`);
      } else {
        toast.success(`$${amt.toFixed(2)} store credit added — new balance $${res.data.store_credit.toFixed(2)}`);
      }
      setCreditCustomer({ ...creditCustomer, store_credit: res.data.store_credit });
      setCreditAmount('');
      setCreditReason('');
      setCreditPin('');
      fetchCustomers(); fetchStats();
      const hist = await axios.get(`${API_URL}/customers/${creditCustomer.id}/store-credit/history`, { headers: getAuthHeader() });
      setCreditHistory(hist.data || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || (creditMode === 'debt' ? 'Failed to record debt' : 'Failed to add store credit'));
    } finally {
      setCreditSubmitting(false);
    }
  };

  const fetchCustomers = async (q = searchTerm) => {
    try {
      const params = new URLSearchParams({ limit: '100' });
      if (q && q.trim()) params.set('q', q.trim());
      const response = await axios.get(`${API_URL}/customers?${params}`, { headers: getAuthHeader() });
      setCustomers(response.data);
    } catch (error) {
      toast.error('Failed to load customers');
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const res = await axios.get(`${API_URL}/customers/stats`, { headers: getAuthHeader() });
      setStats(res.data);
    } catch (_) { /* non-blocking */ }
  };

  useEffect(() => {
    fetchCustomers('');
    fetchStats();
  }, []);

  // Debounced server-side search — keeps typing instant with thousands of customers
  useEffect(() => {
    const t = setTimeout(() => fetchCustomers(searchTerm), 300);
    return () => clearTimeout(t);
  }, [searchTerm]);

  const handleAddCustomer = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.phone) {
      toast.error('Name and phone are required');
      return;
    }
    // Soft nudge — staff often skip the email and lose the marketing channel.
    if (!formData.email || !formData.email.trim()) {
      const ok = window.confirm(
        'Have you asked them for their email address?\n\n' +
        'Emails let us send marketing campaigns (sales, birthday offers, new product alerts) — that\'s how loyalty members come back in.\n\n' +
        'OK = save without email\nCancel = go back and type it in'
      );
      if (!ok) return;
    }

    setFormLoading(true);
    try {
      await axios.post(`${API_URL}/customers`, formData, { headers: getAuthHeader() });
      toast.success('Customer added');
      setIsAddDialogOpen(false);
      setFormData({ name: '', phone: '', email: '', marketing_opt_in: false, notes: '' });
      fetchCustomers(); fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add customer');
    } finally {
      setFormLoading(false);
    }
  };
  
  const handleEditCustomer = (customer) => {
    setEditingCustomer(customer);
    setFormData({
      name: customer.name || '',
      phone: customer.phone || '',
      email: customer.email || '',
      marketing_opt_in: customer.marketing_opt_in || false,
      loyalty_points: customer.loyalty_points || 0,
      notes: customer.notes || ''
    });
    setIsEditDialogOpen(true);
  };
  
  const handleUpdateCustomer = async (e) => {
    e.preventDefault();
    if (!editingCustomer) return;
    
    setFormLoading(true);
    try {
      await axios.put(`${API_URL}/customers/${editingCustomer.id}`, formData, { headers: getAuthHeader() });
      toast.success('Customer updated');
      setIsEditDialogOpen(false);
      setEditingCustomer(null);
      setFormData({ name: '', phone: '', email: '', marketing_opt_in: false, notes: '' });
      fetchCustomers(); fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update customer');
    } finally {
      setFormLoading(false);
    }
  };
  
  const handleToggleMarketing = async (customer) => {
    try {
      await axios.put(`${API_URL}/customers/${customer.id}`, {
        ...customer,
        marketing_opt_in: !customer.marketing_opt_in
      }, { headers: getAuthHeader() });
      toast.success(customer.marketing_opt_in ? 'Removed from marketing list' : 'Added to marketing list');
      fetchCustomers(); fetchStats();
    } catch (error) {
      toast.error('Failed to update marketing preference');
    }
  };

  const handleDeleteCustomer = async (customerId) => {
    if (!window.confirm('Delete this customer?')) return;
    try {
      await axios.delete(`${API_URL}/customers/${customerId}`, { headers: getAuthHeader() });
      toast.success('Customer deleted');
      fetchCustomers(); fetchStats();
    } catch (error) {
      toast.error('Failed to delete customer');
    }
  };

  // Parse CSV/text data for import (Lightspeed format)
  const parseImportData = (text) => {
    const lines = text.trim().split('\n');
    const parsed = [];
    
    // Check if first line is header
    const firstLine = lines[0].toLowerCase();
    const hasHeader = firstLine.includes('name') || firstLine.includes('email') || firstLine.includes('phone') || firstLine.includes('mobile');
    const startIndex = hasHeader ? 1 : 0;
    
    for (let i = startIndex; i < lines.length; i++) {
      const line = lines[i].trim();
      if (!line) continue;
      
      // Parse CSV (supports: first_name, last_name, mobile, email, loyalty_balance)
      const parts = line.split(/[,\t]/).map(p => p.trim().replace(/^["']|["']$/g, ''));
      
      if (parts.length >= 2) {
        // Try to detect column format
        const hasAt = parts.some(p => p.includes('@'));
        const phoneIdx = parts.findIndex(p => /^[0-9+\-\s()]{7,}$/.test(p));
        const emailIdx = parts.findIndex(p => p.includes('@'));
        const loyaltyIdx = parts.findIndex((p, idx) => idx > 1 && /^\d+(\.\d+)?$/.test(p) && !p.includes('@'));
        
        // Assume format: FirstName, LastName, Mobile, Email, LoyaltyBalance
        parsed.push({
          first_name: parts[0] || '',
          last_name: parts[1] || '',
          phone: parts[2] || (phoneIdx >= 0 ? parts[phoneIdx] : ''),
          email: parts[3] || (emailIdx >= 0 ? parts[emailIdx] : ''),
          loyalty_points: parseInt(parts[4]) || (loyaltyIdx >= 0 ? parseInt(parts[loyaltyIdx]) : 0),
          name: `${parts[0] || ''} ${parts[1] || ''}`.trim()
        });
      }
    }
    
    return parsed;
  };

  // Export customers to CSV with purchase history
  const handleExportCustomers = async () => {
    setExporting(true);
    try {
      const response = await axios.get(`${API_URL}/customers/export`, { headers: getAuthHeader() });
      const data = response.data;
      
      // Build CSV
      const headers = ['First Name', 'Last Name', 'Mobile', 'Email', 'Loyalty Balance', 'Products Bought (Last Year)'];
      const rows = data.map(c => [
        c.first_name || c.name?.split(' ')[0] || '',
        c.last_name || c.name?.split(' ').slice(1).join(' ') || '',
        c.phone || '',
        c.email || '',
        c.loyalty_points || 0,
        (c.products_bought || []).join('; ')
      ]);
      
      const csv = [headers.join(','), ...rows.map(r => r.map(v => `"${v}"`).join(','))].join('\n');
      
      // Download file
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `customers_export_${new Date().toISOString().slice(0,10)}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
      
      toast.success(`Exported ${data.length} customers`);
      setIsExportDialogOpen(false);
    } catch (error) {
      toast.error('Export failed');
    } finally {
      setExporting(false);
    }
  };

  const handlePreviewImport = () => {
    const parsed = parseImportData(importData);
    if (parsed.length === 0) {
      toast.error('No valid data found. Use format: First Name, Last Name, Mobile, Email, Loyalty Balance');
      return;
    }
    setImportPreview(parsed);
    setImportStep(2);
  };

  const handleImportCustomers = async () => {
    if (importPreview.length === 0) return;

    setFormLoading(true);
    const total = importPreview.length;
    const CHUNK = 200;
    let done = 0;
    let inserted = 0;
    let dupes = 0;
    let failed = 0;

    setImportProgress({ done: 0, total, inserted: 0, dupes: 0, failed: 0 });

    for (let i = 0; i < total; i += CHUNK) {
      const chunk = importPreview.slice(i, i + CHUNK).map(c => ({
        name: c.name || `${c.first_name || ''} ${c.last_name || ''}`.trim(),
        phone: c.phone || '',
        email: c.email || '',
        loyalty_points: c.loyalty_points || 0,
      }));
      try {
        const res = await axios.post(
          `${API_URL}/customers/bulk-import`,
          { customers: chunk },
          { headers: getAuthHeader(), timeout: 60000 }
        );
        inserted += res.data?.inserted || 0;
        dupes += res.data?.skipped_duplicates || 0;
        failed += res.data?.skipped_invalid || 0;
      } catch (err) {
        console.error('Bulk import chunk failed:', err);
        failed += chunk.length;
      }
      done += chunk.length;
      setImportProgress({ done, total, inserted, dupes, failed });
    }

    toast.success(`Imported ${inserted} customers (${dupes} duplicates skipped${failed ? `, ${failed} invalid` : ''})`);
    setIsImportDialogOpen(false);
    setImportData('');
    setImportPreview([]);
    setImportStep(1);
    setImportProgress({ done: 0, total: 0, inserted: 0, dupes: 0, failed: 0 });
    setFormLoading(false);
    fetchCustomers(); fetchStats();
  };

  const downloadTemplate = () => {
    const template = `First Name,Last Name,Mobile,Email,Loyalty Balance
John,Smith,+64 21 123 4567,john@example.com,150
Jane,Doe,+64 22 987 6543,jane@example.com,200
Bob,Wilson,021 555 1234,,0`;
    
    const blob = new Blob([template], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'customer_import_template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  // List is already filtered server-side (debounced) — no client-side re-filtering on each keystroke
  const filteredCustomers = customers;

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="w-8 h-8 border-4 border-slate-900 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="customers-page">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold uppercase tracking-tight text-slate-900">
            Customers
          </h1>
          <p className="text-slate-500 mt-1">Manage your customer database and loyalty credit</p>
        </div>
        <div className="flex gap-2">
          {/* Export Button */}
          <Button 
            variant="outline" 
            onClick={handleExportCustomers}
            disabled={exporting}
            data-testid="export-customers-button"
          >
            {exporting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
            Export to CSV
          </Button>
          
          <Dialog open={isImportDialogOpen} onOpenChange={(open) => {
            setIsImportDialogOpen(open);
            if (!open) { setImportStep(1); setImportData(''); setImportPreview([]); }
          }}>
            <DialogTrigger asChild>
              <Button variant="outline" data-testid="import-customers-button">
                <Upload className="w-4 h-4 mr-2" /> Import from Lightspeed
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle className="font-heading text-xl font-bold uppercase">
                  Import Customers
                </DialogTitle>
                <DialogDescription>
                  Import customers from Lightspeed (First Name, Last Name, Mobile, Email, Loyalty Balance)
                </DialogDescription>
              </DialogHeader>

              {importStep === 1 ? (
                <div className="space-y-4 mt-4">
                  <div className="flex justify-between items-center">
                    <Label>Paste customer data (CSV format)</Label>
                    <Button variant="outline" size="sm" onClick={downloadTemplate}>
                      <Download className="w-4 h-4 mr-1" /> Template
                    </Button>
                  </div>
                  
                  <textarea
                    className="w-full h-64 p-3 border rounded-sm font-mono text-sm"
                    placeholder={`Paste from Lightspeed export:\n\nFirst Name, Last Name, Mobile, Email, Loyalty Balance\nJohn, Smith, +64 21 123 4567, john@example.com, 150\nJane, Doe, 022 987 6543, jane@test.com, 200\n...`}
                    value={importData}
                    onChange={(e) => setImportData(e.target.value)}
                    data-testid="import-textarea"
                  />
                  
                  <div className="p-3 bg-blue-50 rounded-sm text-sm text-blue-700">
                    <p className="font-medium mb-1">Lightspeed Import Format:</p>
                    <ul className="list-disc list-inside space-y-1">
                      <li>First Name, Last Name, Mobile, Email, Loyalty Balance</li>
                      <li>Tab-separated values from Excel also supported</li>
                      <li>Header row is auto-detected and skipped</li>
                    </ul>
                  </div>
                  
                  <Button 
                    onClick={handlePreviewImport} 
                    className="w-full bg-slate-900"
                    disabled={!importData.trim()}
                  >
                    Preview Import
                  </Button>
                </div>
              ) : (
                <div className="space-y-4 mt-4">
                  <div className="flex items-center justify-between">
                    <Badge variant="outline" className="text-lg">
                      {importPreview.length} customers to import
                    </Badge>
                    <Button variant="ghost" size="sm" onClick={() => setImportStep(1)}>
                      Back to Edit
                    </Button>
                  </div>
                  
                  <ScrollArea className="h-64 border rounded-sm">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Name</TableHead>
                          <TableHead>Phone</TableHead>
                          <TableHead>Email</TableHead>
                          <TableHead className="w-16">Valid</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {importPreview.map((customer, idx) => (
                          <TableRow key={idx}>
                            <TableCell>{customer.name}</TableCell>
                            <TableCell>{customer.phone}</TableCell>
                            <TableCell>{customer.email || '-'}</TableCell>
                            <TableCell>
                              {customer.phone ? (
                                <Check className="w-4 h-4 text-emerald-500" />
                              ) : (
                                <X className="w-4 h-4 text-red-500" />
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </ScrollArea>
                  
                  {formLoading && importProgress?.total > 0 && (
                    <div className="border-2 border-emerald-200 bg-emerald-50 rounded-md p-3 mb-2">
                      <div className="flex justify-between text-xs font-semibold text-emerald-800 mb-1">
                        <span>Importing... {importProgress.done} / {importProgress.total}</span>
                        <span>{Math.round((importProgress.done / importProgress.total) * 100)}%</span>
                      </div>
                      <div className="w-full bg-emerald-200 rounded-full h-2 overflow-hidden">
                        <div
                          className="bg-emerald-600 h-2 transition-all duration-300"
                          style={{ width: `${(importProgress.done / importProgress.total) * 100}%` }}
                        />
                      </div>
                      <div className="flex gap-3 mt-2 text-[11px] text-emerald-700">
                        <span>✓ {importProgress.inserted} imported</span>
                        <span className="text-amber-700">↻ {importProgress.dupes} duplicates</span>
                        {importProgress.failed > 0 && <span className="text-red-700">✕ {importProgress.failed} invalid</span>}
                      </div>
                    </div>
                  )}

                  <Button 
                    onClick={handleImportCustomers}
                    disabled={formLoading}
                    className="w-full bg-emerald-600 hover:bg-emerald-700"
                  >
                    {formLoading ? (
                      <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Importing...</>
                    ) : (
                      <><Upload className="w-4 h-4 mr-2" /> Import {importPreview.length} Customers</>
                    )}
                  </Button>
                </div>
              )}
            </DialogContent>
          </Dialog>

          <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-orange-500 hover:bg-orange-600" data-testid="add-customer-button">
                <Plus className="w-4 h-4 mr-2" /> Add Customer
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle className="font-heading text-xl font-bold uppercase">Add Customer</DialogTitle>
                <DialogDescription>Add a new customer to your loyalty program</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleAddCustomer} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label>Name *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="John Smith"
                    required
                    data-testid="customer-name"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Phone *</Label>
                  <Input
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="+64 21 123 4567"
                    required
                    data-testid="customer-phone"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Email</Label>
                  <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900 flex items-start gap-2" data-testid="email-nudge-customers">
                    <span className="text-base leading-none">📧</span>
                    <span><b>Always ask for their email!</b> It's how marketing campaigns reach them — birthday offers, sales, new product alerts. Loyalty without email = they don't come back.</span>
                  </div>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="john@example.com  ← please ask!"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Health Notes</Label>
                  <Textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder="e.g. Taking blood thinners (warfarin) — no fish oil. Goal: better sleep + gut health."
                    rows={3}
                    data-testid="customer-notes-input"
                  />
                  <p className="text-xs text-slate-500">Medications, allergies, health goals — shows on their file and at the POS.</p>
                </div>
                <div className="flex items-center gap-3 p-3 bg-purple-50 rounded-lg border border-purple-100">
                  <input
                    type="checkbox"
                    id="marketing-opt-in"
                    checked={formData.marketing_opt_in}
                    onChange={(e) => setFormData({ ...formData, marketing_opt_in: e.target.checked })}
                    className="w-5 h-5 rounded border-purple-300 text-purple-600 focus:ring-purple-500"
                  />
                  <label htmlFor="marketing-opt-in" className="flex items-center gap-2 cursor-pointer">
                    <Megaphone className="w-4 h-4 text-purple-600" />
                    <span className="font-medium text-purple-900">Join Marketing List</span>
                  </label>
                </div>
                <div className="flex justify-end gap-2 pt-4">
                  <Button type="button" variant="outline" onClick={() => setIsAddDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={formLoading} className="bg-slate-900">
                    {formLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Add Customer'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
          
          {/* Edit Customer Dialog */}
          <Dialog open={isEditDialogOpen} onOpenChange={(open) => {
            setIsEditDialogOpen(open);
            if (!open) { setEditingCustomer(null); setFormData({ name: '', phone: '', email: '', marketing_opt_in: false, notes: '' }); }
          }}>
            <DialogContent>
              <DialogHeader>
                <DialogTitle className="font-heading text-xl font-bold uppercase">Edit Customer</DialogTitle>
                <DialogDescription>Update customer details</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleUpdateCustomer} className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label>Name *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="John Smith"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Phone *</Label>
                  <Input
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    placeholder="+64 21 123 4567"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Email</Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="john@example.com"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Health Notes</Label>
                  <Textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    placeholder="e.g. Taking blood thinners (warfarin) — no fish oil. Goal: better sleep + gut health."
                    rows={3}
                    data-testid="edit-customer-notes-input"
                  />
                  <p className="text-xs text-slate-500">Medications, allergies, health goals — shows on their file and at the POS.</p>
                </div>
                <div className="flex items-center gap-3 p-3 bg-purple-50 rounded-lg border border-purple-100">
                  <input
                    type="checkbox"
                    id="edit-marketing-opt-in"
                    checked={formData.marketing_opt_in}
                    onChange={(e) => setFormData({ ...formData, marketing_opt_in: e.target.checked })}
                    className="w-5 h-5 rounded border-purple-300 text-purple-600 focus:ring-purple-500"
                  />
                  <label htmlFor="edit-marketing-opt-in" className="flex items-center gap-2 cursor-pointer">
                    <Megaphone className="w-4 h-4 text-purple-600" />
                    <span className="font-medium text-purple-900">Join Marketing List</span>
                  </label>
                </div>
                <div className="flex justify-end gap-2 pt-4">
                  <Button type="button" variant="outline" onClick={() => setIsEditDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={formLoading} className="bg-slate-900">
                    {formLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Save Changes'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="border rounded-sm">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
              <Users className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">{stats.total}</p>
              <p className="text-sm text-slate-500">Total Customers</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border rounded-sm">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
              <Megaphone className="w-6 h-6 text-purple-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">
                {stats.marketing_subscribers}
              </p>
              <p className="text-sm text-slate-500">Marketing Subscribers</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border rounded-sm">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-12 h-12 bg-yellow-100 rounded-full flex items-center justify-center">
              <Star className="w-6 h-6 text-yellow-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">
                {(stats.total_loyalty || 0).toLocaleString('en-NZ', {style: 'currency', currency: 'NZD', maximumFractionDigits: 0})}
              </p>
              <p className="text-sm text-slate-500">Total Loyalty Credit Issued</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border rounded-sm">
          <CardContent className="p-4 flex items-center gap-4">
            <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center">
              <User className="w-6 h-6 text-emerald-600" />
            </div>
            <div>
              <p className="text-2xl font-bold">
                {stats.active_members}
              </p>
              <p className="text-sm text-slate-500">Active Members (100+ pts)</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input
          placeholder="Search by name, phone or email..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-9"
          data-testid="customer-search"
        />
      </div>

      {/* Customers Table */}
      <Card className="border rounded-sm">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="font-heading font-bold uppercase text-xs">Customer</TableHead>
                <TableHead className="font-heading font-bold uppercase text-xs">Phone</TableHead>
                <TableHead className="font-heading font-bold uppercase text-xs">Email</TableHead>
                <TableHead className="font-heading font-bold uppercase text-xs text-center">Marketing</TableHead>
                <TableHead className="font-heading font-bold uppercase text-xs text-center">Loyalty Credit ($)</TableHead>
                <TableHead className="font-heading font-bold uppercase text-xs text-center">Store Credit</TableHead>
                <TableHead className="font-heading font-bold uppercase text-xs">Joined</TableHead>
                <TableHead className="w-24"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredCustomers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-12 text-slate-500">
                    <Users className="w-12 h-12 mx-auto mb-3 text-slate-300" />
                    {searchTerm ? 'No customers match your search' : 'No customers yet'}
                  </TableCell>
                </TableRow>
              ) : (
                filteredCustomers.map((customer) => (
                  <TableRow key={customer.id} data-testid={`customer-row-${customer.id}`}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center">
                          <User className="w-5 h-5 text-slate-500" />
                        </div>
                        <div>
                          <span className="font-medium">{customer.name}</span>
                          {customer.notes && (
                            <p className="text-xs text-teal-700 flex items-center gap-1 mt-0.5 max-w-[260px] truncate" title={customer.notes} data-testid={`customer-notes-${customer.id}`}>
                              <FileText className="w-3 h-3 shrink-0" /> {customer.notes}
                            </p>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2 text-slate-600">
                        <Phone className="w-4 h-4" />
                        {customer.phone}
                      </div>
                    </TableCell>
                    <TableCell>
                      {customer.email ? (
                        <div className="flex items-center gap-2 text-slate-600">
                          <Mail className="w-4 h-4" />
                          {customer.email}
                        </div>
                      ) : (
                        <span className="text-slate-400">-</span>
                      )}
                    </TableCell>
                    <TableCell className="text-center">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleToggleMarketing(customer)}
                        className={customer.marketing_opt_in ? 'text-purple-600 hover:text-purple-700' : 'text-slate-400 hover:text-slate-600'}
                        title={customer.marketing_opt_in ? 'Remove from marketing' : 'Add to marketing'}
                      >
                        <Megaphone className={`w-5 h-5 ${customer.marketing_opt_in ? 'fill-purple-100' : ''}`} />
                      </Button>
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge 
                        variant="outline" 
                        className={`${(customer.loyalty_points || 0) >= 100 
                          ? 'bg-yellow-50 text-yellow-700 border-yellow-200' 
                          : 'bg-slate-50'}`}
                      >
                        <Star className="w-3 h-3 mr-1" />
                        ${customer.loyalty_points || 0}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-center">
                      <Badge
                        variant="outline"
                        className={`${(customer.store_credit || 0) > 0
                          ? 'bg-amber-50 text-amber-700 border-amber-200'
                          : (customer.store_credit || 0) < 0
                            ? 'bg-red-50 text-red-700 border-red-300 font-bold'
                            : 'bg-slate-50 text-slate-400'}`}
                        data-testid={`store-credit-badge-${customer.id}`}
                      >
                        <Wallet className="w-3 h-3 mr-1" />
                        {(customer.store_credit || 0) < 0
                          ? `OWES $${Math.abs(customer.store_credit).toFixed(2)}`
                          : `$${(customer.store_credit || 0).toFixed(2)}`}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-slate-500 text-sm">
                      {formatDate(customer.created_at)}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openHistoryDialog(customer)}
                          className="text-indigo-600 hover:text-indigo-800"
                          title="View purchase history"
                          data-testid={`customer-history-${customer.id}`}
                        >
                          <History className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openStoreCreditDialog(customer)}
                          className="text-amber-600 hover:text-amber-800"
                          title="Store credit"
                          data-testid={`customer-store-credit-${customer.id}`}
                        >
                          <Wallet className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEditCustomer(customer)}
                          className="text-slate-500 hover:text-slate-700"
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDeleteCustomer(customer.id)}
                          className="text-red-500 hover:text-red-700"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          {stats.total > customers.length && (
            <div className="p-3 text-center text-sm text-slate-500 border-t bg-slate-50" data-testid="customers-partial-banner">
              Showing {customers.length} of {stats.total} customers — type a name, phone or email to search everyone
            </div>
          )}
        </CardContent>
      </Card>

      {/* Store Credit Dialog */}
      <Dialog open={!!creditCustomer} onOpenChange={(o) => !o && setCreditCustomer(null)}>
        <DialogContent className="max-w-lg max-h-[88vh] overflow-y-auto" data-testid="store-credit-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-bold uppercase flex items-center gap-2">
              <Wallet className="w-5 h-5 text-amber-600" />
              Store Credit — {creditCustomer?.name}
            </DialogTitle>
            <DialogDescription>
              Add store credit (manager PIN required). Credit is redeemed as a payment method at the POS.
            </DialogDescription>
          </DialogHeader>

          <div className={`p-4 rounded-sm flex justify-between items-center ${(creditCustomer?.store_credit || 0) < 0 ? 'bg-red-50' : 'bg-amber-50'}`}>
            <span className={`font-medium ${(creditCustomer?.store_credit || 0) < 0 ? 'text-red-800' : 'text-amber-800'}`}>
              {(creditCustomer?.store_credit || 0) < 0 ? 'Customer OWES' : 'Current Balance'}
            </span>
            <span className={`text-3xl font-bold ${(creditCustomer?.store_credit || 0) < 0 ? 'text-red-700' : 'text-amber-700'}`} data-testid="store-credit-dialog-balance">
              ${(creditCustomer?.store_credit || 0).toFixed(2)}
            </span>
          </div>

          <div className="grid grid-cols-2 gap-2">
            <Button
              type="button"
              variant={creditMode === 'add' ? 'default' : 'outline'}
              onClick={() => setCreditMode('add')}
              className={creditMode === 'add' ? 'bg-amber-600 hover:bg-amber-700' : ''}
              data-testid="store-credit-mode-add"
            >
              <Plus className="w-4 h-4 mr-1" /> Add Credit
            </Button>
            <Button
              type="button"
              variant={creditMode === 'debt' ? 'default' : 'outline'}
              onClick={() => setCreditMode('debt')}
              className={creditMode === 'debt' ? 'bg-red-600 hover:bg-red-700' : 'border-red-200 text-red-700 hover:bg-red-50'}
              data-testid="store-credit-mode-debt"
            >
              <X className="w-4 h-4 mr-1" /> Record Debt (owes us)
            </Button>
          </div>

          {creditMode === 'debt' && (
            <p className="text-xs text-red-600 -mt-1">
              Records money this customer owes you (e.g. their card declined). Their balance goes negative until they pay it back.
            </p>
          )}

          <form onSubmit={handleAddStoreCredit} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Amount ($)</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0.01"
                  value={creditAmount}
                  onChange={(e) => setCreditAmount(e.target.value)}
                  placeholder="0.00"
                  data-testid="store-credit-add-amount"
                />
              </div>
              <div>
                <Label>Manager PIN</Label>
                <Input
                  type="password"
                  value={creditPin}
                  onChange={(e) => setCreditPin(e.target.value)}
                  placeholder="PIN"
                  data-testid="store-credit-manager-pin"
                />
              </div>
            </div>
            <div>
              <Label>Reason</Label>
              <Input
                value={creditReason}
                onChange={(e) => setCreditReason(e.target.value)}
                placeholder={creditMode === 'debt' ? 'e.g. Card declined 24/07, took goods...' : 'e.g. Returned item, goodwill, promo...'}
                data-testid="store-credit-reason"
              />
            </div>
            <Button
              type="submit"
              disabled={creditSubmitting}
              className={`w-full ${creditMode === 'debt' ? 'bg-red-600 hover:bg-red-700' : 'bg-amber-600 hover:bg-amber-700'}`}
              data-testid="store-credit-add-btn"
            >
              {creditSubmitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
              {creditMode === 'debt' ? 'Record Debt' : 'Add Store Credit'}
            </Button>
          </form>

          <Separator />

          <div>
            <h4 className="font-heading font-bold uppercase text-sm mb-2 flex items-center gap-2">
              <History className="w-4 h-4" /> Credit History
            </h4>
            {creditHistoryLoading ? (
              <div className="flex justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
            ) : creditHistory.length === 0 ? (
              <p className="text-sm text-slate-400 text-center py-4">No store credit activity yet</p>
            ) : (
              <div className="space-y-2 max-h-64 overflow-y-auto" data-testid="store-credit-history-list">
                {creditHistory.map((entry) => (
                  <div key={entry.id} className="flex justify-between items-start p-2 border rounded-sm text-sm">
                    <div>
                      <p className={`font-medium ${entry.type === 'add' ? 'text-emerald-700' : 'text-red-600'}`}>
                        {entry.type === 'add' ? '+' : '−'}${entry.amount.toFixed(2)}
                        {entry.type === 'debt' && <span className="text-xs bg-red-100 text-red-700 px-1.5 py-0.5 rounded ml-2">DEBT</span>}
                        <span className="text-slate-500 font-normal ml-2">{entry.reason || (entry.type === 'add' ? 'Credit added' : 'Redeemed')}</span>
                      </p>
                      <p className="text-xs text-slate-400">
                        {formatDateTime(entry.created_at)}{entry.staff_name ? ` · ${entry.staff_name}` : ''}
                      </p>
                    </div>
                    <span className="text-xs text-slate-500 whitespace-nowrap">Bal ${entry.balance_after.toFixed(2)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Purchase History Dialog */}
      <Dialog open={!!historyCustomer} onOpenChange={(o) => !o && setHistoryCustomer(null)}>
        <DialogContent className="max-w-4xl max-h-[88vh] overflow-y-auto" data-testid="customer-history-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-bold uppercase flex items-center gap-2">
              <History className="w-5 h-5 text-indigo-600" />
              Purchase History — {historyCustomer?.name}
            </DialogTitle>
            <DialogDescription>
              Every sale recorded under this customer, newest first. Use this when an older customer asks "what did I buy last time?"
            </DialogDescription>
          </DialogHeader>

          {/* Reorder: purchase history LIST first (what the user opens this
              dialog to see), then the "Add Past Purchase" attach panel below.
              Using flex-col-reverse so the sibling markup order stays natural
              in code but renders in the order the user actually wants. */}
          <div className="flex flex-col-reverse gap-4">

          {/* Add Past Purchase (retroactive loyalty attach) — always visible */}
          <div className="border-2 border-emerald-200 bg-emerald-50/40 rounded-lg p-3 mb-4" data-testid="add-past-purchase-panel">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-emerald-600" />
                <div>
                  <p className="font-semibold text-sm text-emerald-800">Add a Past Purchase to Loyalty</p>
                  <p className="text-xs text-slate-600">
                    Customer forgot to give their phone at the till? Find the sale here and credit their points. ($100 = 3 points)
                  </p>
                </div>
              </div>
              <Button
                size="sm"
                variant={attachOpen ? 'secondary' : 'default'}
                className={attachOpen ? '' : 'bg-emerald-600 hover:bg-emerald-700 text-white'}
                onClick={() => setAttachOpen(!attachOpen)}
                data-testid="toggle-add-past-purchase"
              >
                {attachOpen ? 'Hide' : (<><Plus className="w-3.5 h-3.5 mr-1" />Find Past Sale</>)}
              </Button>
            </div>

            {attachOpen && (
              <div className="mt-3 space-y-3">
                {/* Mode tabs */}
                <div className="flex gap-1 border-b">
                  {[
                    { id: 'recent', label: 'Recent Unattached', icon: History },
                    { id: 'receipt', label: 'By Receipt #', icon: Receipt },
                    { id: 'datetime', label: 'By Date + Amount', icon: Search },
                  ].map((t) => {
                    const Icon = t.icon;
                    const active = attachMode === t.id;
                    return (
                      <button
                        key={t.id}
                        onClick={() => { setAttachMode(t.id); setAttachResults([]); }}
                        className={`flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border-b-2 ${active ? 'border-emerald-600 text-emerald-700' : 'border-transparent text-slate-500 hover:text-slate-700'}`}
                        data-testid={`attach-mode-${t.id}`}
                      >
                        <Icon className="w-3.5 h-3.5" />
                        {t.label}
                      </button>
                    );
                  })}
                </div>

                {/* Search inputs per mode */}
                {attachMode === 'recent' && (
                  <div className="flex gap-2 items-end">
                    <p className="text-xs text-slate-500 flex-1">
                      Shows the most recent sales that have no customer attached. Pick the one that's theirs.
                    </p>
                    <Button
                      size="sm"
                      onClick={() => runAttachSearch('recent')}
                      disabled={attachLoading}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white"
                      data-testid="attach-search-recent-btn"
                    >
                      {attachLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Load Recent Unattached'}
                    </Button>
                  </div>
                )}

                {attachMode === 'receipt' && (
                  <div className="flex gap-2 items-end">
                    <div className="flex-1">
                      <Label className="text-[11px] uppercase text-slate-500">Receipt / Sale ID</Label>
                      <Input
                        value={attachSearch.sale_id}
                        onChange={(e) => setAttachSearch({ ...attachSearch, sale_id: e.target.value })}
                        placeholder="Paste full sale ID (printed on receipt)"
                        className="h-9 text-sm"
                        data-testid="attach-receipt-input"
                      />
                    </div>
                    <Button
                      size="sm"
                      onClick={() => runAttachSearch('receipt')}
                      disabled={attachLoading}
                      className="bg-emerald-600 hover:bg-emerald-700 text-white"
                      data-testid="attach-search-receipt-btn"
                    >
                      {attachLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Find'}
                    </Button>
                  </div>
                )}

                {attachMode === 'datetime' && (
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-2 items-end">
                    <div>
                      <Label className="text-[11px] uppercase text-slate-500">From</Label>
                      <Input
                        type="date"
                        value={attachSearch.start_date}
                        onChange={(e) => setAttachSearch({ ...attachSearch, start_date: e.target.value })}
                        className="h-9 text-sm"
                        data-testid="attach-start-date"
                      />
                    </div>
                    <div>
                      <Label className="text-[11px] uppercase text-slate-500">To</Label>
                      <Input
                        type="date"
                        value={attachSearch.end_date}
                        onChange={(e) => setAttachSearch({ ...attachSearch, end_date: e.target.value })}
                        className="h-9 text-sm"
                        data-testid="attach-end-date"
                      />
                    </div>
                    <div>
                      <Label className="text-[11px] uppercase text-slate-500">Min $</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={attachSearch.min_amount}
                        onChange={(e) => setAttachSearch({ ...attachSearch, min_amount: e.target.value })}
                        placeholder="0"
                        className="h-9 text-sm"
                        data-testid="attach-min-amount"
                      />
                    </div>
                    <div>
                      <Label className="text-[11px] uppercase text-slate-500">Max $</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={attachSearch.max_amount}
                        onChange={(e) => setAttachSearch({ ...attachSearch, max_amount: e.target.value })}
                        placeholder="∞"
                        className="h-9 text-sm"
                        data-testid="attach-max-amount"
                      />
                    </div>
                    <div className="col-span-2 md:col-span-1 flex gap-2">
                      <select
                        value={attachSearch.store}
                        onChange={(e) => setAttachSearch({ ...attachSearch, store: e.target.value })}
                        className="h-9 text-sm border border-slate-300 rounded px-2 flex-1"
                        data-testid="attach-store-filter"
                      >
                        <option value="">All stores</option>
                        <option value="orewa">{storeName('orewa')}</option>
                        <option value="eastridge">{storeName('eastridge')}</option>
                      </select>
                      <Button
                        size="sm"
                        onClick={() => runAttachSearch('datetime')}
                        disabled={attachLoading}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white"
                        data-testid="attach-search-datetime-btn"
                      >
                        {attachLoading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Search'}
                      </Button>
                    </div>
                  </div>
                )}

                {/* Results */}
                {attachResults.length > 0 && (
                  <div className="border rounded bg-white max-h-72 overflow-y-auto" data-testid="attach-results">
                    {attachResults.map((s) => {
                      const dt = s.created_at ? new Date(s.created_at) : null;
                      const alreadyAttached = !!s.customer_id;
                      const isAttaching = attaching === s.id;
                      const points = previewPoints(s.total_amount);
                      const showPin = pinPromptSaleId === s.id;
                      return (
                        <div key={s.id} className="border-b last:border-b-0 p-2.5 hover:bg-slate-50" data-testid={`attach-result-${s.id}`}>
                          <div className="flex items-center justify-between gap-2">
                            <div className="flex-1 text-xs">
                              <div className="flex items-center gap-2 flex-wrap">
                                <span className="font-semibold text-slate-700">
                                  {dt ? dt.toLocaleDateString('en-NZ') : '-'} {dt ? dt.toLocaleTimeString('en-NZ', { hour: '2-digit', minute: '2-digit' }) : ''}
                                </span>
                                <Badge variant="outline" className="capitalize text-[10px]">{s.store || '-'}</Badge>
                                <Badge variant="outline" className="capitalize text-[10px]">{s.payment_method || '-'}</Badge>
                                <span className="font-bold text-emerald-700">${(s.total_amount || 0).toFixed(2)}</span>
                                <span className="text-slate-400">→ +{points} pts</span>
                                {alreadyAttached && (
                                  <Badge className="bg-amber-100 text-amber-800 text-[10px]" data-testid={`attach-already-${s.id}`}>
                                    Already attached to {s.customer_name || 'another customer'}
                                  </Badge>
                                )}
                              </div>
                              <p className="text-slate-500 mt-0.5 truncate">
                                {(s.items || []).length} item{(s.items || []).length !== 1 ? 's' : ''}: {(s.items || []).slice(0, 3).map((i) => i.product_name || i.name).join(', ')}
                                {(s.items || []).length > 3 ? '…' : ''}
                              </p>
                              <p className="text-[10px] text-slate-400 font-mono">{s.id}</p>
                            </div>
                            {!showPin && (
                              <Button
                                size="sm"
                                onClick={() => attachSaleToCustomer(s.id)}
                                disabled={isAttaching}
                                className={alreadyAttached ? 'bg-amber-600 hover:bg-amber-700 text-white' : 'bg-emerald-600 hover:bg-emerald-700 text-white'}
                                data-testid={`attach-btn-${s.id}`}
                              >
                                {isAttaching ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : (alreadyAttached ? 'Re-attach' : 'Attach')}
                              </Button>
                            )}
                          </div>
                          {showPin && (
                            <div className="mt-2 p-2 bg-amber-50 border border-amber-300 rounded flex items-center gap-2" data-testid={`attach-pin-prompt-${s.id}`}>
                              <Input
                                type="password"
                                value={pinValue}
                                onChange={(e) => setPinValue(e.target.value)}
                                placeholder="Manager PIN to re-attach"
                                className="h-8 text-sm max-w-[200px]"
                                autoFocus
                                data-testid={`attach-pin-input-${s.id}`}
                              />
                              <Button
                                size="sm"
                                onClick={() => attachSaleToCustomer(s.id, pinValue)}
                                disabled={isAttaching || !pinValue}
                                className="bg-amber-600 hover:bg-amber-700 text-white"
                                data-testid={`attach-pin-confirm-${s.id}`}
                              >
                                {isAttaching ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Confirm'}
                              </Button>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => { setPinPromptSaleId(null); setPinValue(''); }}
                              >
                                Cancel
                              </Button>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>

          {historyLoading ? (
            <div className="py-12 text-center text-slate-500">Loading…</div>
          ) : historySales.length === 0 ? (
            <div className="py-12 text-center text-slate-500">
              No purchases recorded yet. Make sure to scan their phone / select them at POS before completing a sale.
            </div>
          ) : (
            <div className="space-y-4">
              {/* Top stats */}
              <div className="grid grid-cols-3 gap-3 text-sm">
                <div className="bg-emerald-50 rounded p-3">
                  <p className="text-xs uppercase text-emerald-700 font-semibold">Total Spent</p>
                  <p className="text-xl font-bold text-emerald-700">
                    ${historySales.reduce((s, x) => s + (x.total_amount || 0), 0).toFixed(2)}
                  </p>
                </div>
                <div className="bg-indigo-50 rounded p-3">
                  <p className="text-xs uppercase text-indigo-700 font-semibold"># of Visits</p>
                  <p className="text-xl font-bold text-indigo-700">{historySales.length}</p>
                </div>
                <div className="bg-amber-50 rounded p-3">
                  <p className="text-xs uppercase text-amber-700 font-semibold">Avg Basket</p>
                  <p className="text-xl font-bold text-amber-700">
                    ${(historySales.reduce((s, x) => s + (x.total_amount || 0), 0) / historySales.length).toFixed(2)}
                  </p>
                </div>
              </div>

              {/* Sales accordion */}
              <div className="space-y-2">
                {historySales.map((sale) => {
                  const dt = sale.created_at ? new Date(sale.created_at) : null;
                  return (
                    <details key={sale.id} className="border rounded">
                      <summary className="cursor-pointer p-3 hover:bg-slate-50 flex items-center justify-between gap-3">
                        <div className="flex items-center gap-3">
                          <span className="font-bold text-slate-700">
                            {dt ? dt.toLocaleDateString('en-NZ') : '-'}
                          </span>
                          <span className="text-xs text-slate-500">
                            {dt ? dt.toLocaleTimeString('en-NZ', { hour: '2-digit', minute: '2-digit' }) : ''}
                          </span>
                          <Badge variant="outline" className="capitalize">{sale.payment_method || '-'}</Badge>
                          <Badge variant="outline" className="capitalize">{sale.store || '-'}</Badge>
                          <span className="text-xs text-slate-400">
                            {(sale.items || []).length} item{(sale.items || []).length !== 1 ? 's' : ''}
                          </span>
                        </div>
                        <span className="font-bold text-emerald-700">${(sale.total_amount || 0).toFixed(2)}</span>
                      </summary>
                      <div className="p-3 border-t bg-slate-50">
                        <table className="w-full text-sm">
                          <thead className="text-xs uppercase text-slate-500">
                            <tr>
                              <th className="text-left py-1">Product</th>
                              <th className="text-right py-1">Qty</th>
                              <th className="text-right py-1">Unit</th>
                              <th className="text-right py-1">Line</th>
                            </tr>
                          </thead>
                          <tbody>
                            {(sale.items || []).map((item, idx) => (
                              <tr key={idx} className="border-t border-slate-200">
                                <td className="py-1">{item.product_name || item.name}</td>
                                <td className="py-1 text-right">{item.quantity}</td>
                                <td className="py-1 text-right">${(item.unit_price || 0).toFixed(2)}</td>
                                <td className="py-1 text-right font-medium">${((item.quantity || 0) * (item.unit_price || 0)).toFixed(2)}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        {sale.staff_name && (
                          <p className="text-xs text-slate-400 mt-2">Served by: {sale.staff_name}</p>
                        )}
                      </div>
                    </details>
                  );
                })}
              </div>
            </div>
          )}

          </div>{/* end flex-col-reverse wrapper */}

          <div className="flex justify-end pt-2">
            <Button variant="outline" onClick={() => setHistoryCustomer(null)} data-testid="close-customer-history">Close</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Customers;
